import React, { useState } from 'react';
import { X, Database, Network, Dna, ChevronDown, ChevronUp } from 'lucide-react';

interface AboutModalProps {
  onClose: () => void;
}

const authors = [
  {
    name: 'Simarjot Pabla',
    role: 'Platform Architecture & Engineering',
    description: 'Built RNAverse from the ground up. AI system design, backend infrastructure, and full-stack development.',
    gradient: 'from-blue-600 to-indigo-600',
    initials: 'SP',
  },
  {
    name: 'Yihan Wang',
    role: 'lncRNA Biology & Data Processing',
    description: 'Processes large-scale single-cell data. Supports model workflows and lncRNA annotation.',
    gradient: 'from-emerald-600 to-teal-600',
    initials: 'YW',
  },
  {
    name: 'Vishantie Dostal',
    role: 'Scientific Framework & Platform Strategy',
    description: 'Defined and built the RNAverse scientific framework. Defines how single-cell data becomes gene regulatory networks and therapeutic targets.',
    gradient: 'from-purple-600 to-pink-600',
    initials: 'VD',
  },
  {
    name: 'John Rinn',
    role: 'CEO & Co-Founder, LincSwitch Therapeutics',
    description: 'Pioneer in non-coding RNA biology. Professor at the University of Colorado Boulder, Department of Biochemistry.',
    gradient: 'from-indigo-600 to-purple-600',
    initials: 'JR',
  },
];

interface Method {
  icon: React.ReactNode;
  title: string;
  summary: string;
  detail: React.ReactNode;
}

const methods: Method[] = [
  {
    icon: <Database className="w-5 h-5" />,
    title: 'Single-Cell Atlas',
    summary:
      'Expression statistics computed from CZ CELLxGENE Census — a harmonized collection of millions of single-cell RNA-seq profiles spanning 127+ diseases and hundreds of cell types.',
    detail: (
      <div className="space-y-3 text-xs text-gray-400 leading-relaxed">
        <p>
          <span className="font-semibold text-gray-200">Data source.</span> CZ CELLxGENE Census
          aggregates single-cell RNA-seq studies across the Human Cell Atlas, BRAIN Initiative, and
          dozens of disease cohorts into a single harmonized corpus. RNAverse indexes 127+ disease
          categories, each mapped to one or more CELLxGENE dataset IDs via a curated registry.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Pseudo-bulk aggregation.</span> For each
          disease × cell-type × donor combination, raw UMI counts are summed to produce a
          pseudo-bulk profile. This removes single-cell dropout noise while preserving cell-type
          resolution.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Normalization.</span> Pseudo-bulk counts
          are library-size normalized (counts per million) and log₁p transformed. Differential
          expression between disease and healthy donors is quantified as log₂ fold change (log₂FC)
          per gene per cell type.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Stored statistics.</span> Per-gene
          statistics — mean log₂FC, fraction expressing, and detection rate — are stored as Parquet files in Google Cloud Storage for sub-second retrieval at query time.
          Gene coverage follows GENCODE v44 (protein-coding + non-coding RNA annotations).
        </p>
      </div>
    ),
  },
  {
    icon: <Network className="w-5 h-5" />,
    title: 'Gene Regulatory Networks',
    summary:
      'Co-expression GRNs built per ncRNA × disease × dataset using Pearson correlation on pseudo-bulk profiles, refined with scGPT embeddings, and filtered to top-weighted protein-coding neighbors.',
    detail: (
      <div className="space-y-3 text-xs text-gray-400 leading-relaxed">
        <p>
          <span className="font-semibold text-gray-200">Co-expression backbone.</span> For each
          non-coding RNA target, Pearson correlations are computed between its pseudo-bulk
          expression profile and every protein-coding gene across all samples in the dataset.
          Edges are retained for the top-k neighbors (default k = 50) by absolute correlation
          weight, producing a gene regulatory network (GRN) specific to each ncRNA × disease ×
          dataset triplet.
        </p>
        <p>
          <span className="font-semibold text-gray-200">scGPT fine-tuning.</span> A pre-trained
          scGPT foundation model — trained on tens of millions of single cells — is fine-tuned on
          disease-specific single-cell data to generate context-aware gene embeddings. These
          embeddings capture gene–gene relationships beyond pairwise correlation by encoding
          higher-order transcriptional programs. Fine-tuned embeddings are used to reweight GRN
          edges: pairs of genes whose scGPT embeddings are geometrically close in the disease
          context receive higher edge weights, biologically grounding the network in cell-type
          and disease-relevant co-regulation.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Storage and indexing.</span> Each GRN is
          serialized as a compact JSON file and stored in GCS under
          <code className="mx-1 px-1 py-0.5 bg-gray-800 rounded font-mono">
            precomputed/grn/&#123;ncRNA&#125;_&#123;disease&#125;_&#123;dataset_id&#125;.json
          </code>
          with a master index enabling O(1) lookup by disease at query time.
        </p>
      </div>
    ),
  },
  {
    icon: <Dna className="w-5 h-5" />,
    title: 'In Silico Knockdown',
    summary:
      'Perturbation model propagates expression changes through the GRN. Rescue score measures downstream dysregulation reversal: Σ improvement·|log₂FC| / Σ |log₂FC|.',
    detail: (
      <div className="space-y-3 text-xs text-gray-400 leading-relaxed">
        <p>
          <span className="font-semibold text-gray-200">Synthetic expression matrix.</span> A
          two-condition (disease vs. healthy) expression matrix is constructed on-the-fly from the
          GRN node set and their Parquet log₂FC values. This avoids downloading full
          H5AD files and reduces per-query latency from minutes to seconds.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Perturbation model.</span> The target
          ncRNA's expression is set to zero (complete knockdown). Expression changes are propagated
          one hop through the GRN: each neighbor's post-knockdown expression is estimated as
          <span className="italic mx-1">expr_neighbor × (1 − edge_weight × knockdown_fraction)</span>
          , where edge_weight is the correlation-derived coefficient.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Dysregulation threshold.</span> Genes with
          |log₂FC| ≥ 1.0 are classified as dysregulated in the disease condition. Only these genes
          contribute to the rescue score denominator.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Rescue score.</span> For each dysregulated
          gene <em>i</em>, improvement is defined as
          <span className="mx-1 font-mono text-indigo-400">
            max(0, |FC_before| − |FC_after|) / |FC_before|
          </span>
          . The weighted rescue score is
          <span className="mx-1 font-mono text-indigo-400">
            Σᵢ(improvement_i · |log₂FC_i|) / Σⱼ|log₂FC_j|
          </span>
          — a magnitude-weighted fraction of dysregulation reversed. A score of 1.0 means all
          disease-associated expression changes are fully normalized.
        </p>
        <p>
          <span className="font-semibold text-gray-200">Protein-coding gene estimation.</span>{' '}
          For protein-coding neighbors not directly covered by the ncRNA Parquet (i.e. no
          cell-type expression entry), log₂FC is estimated as
          <span className="mx-1 font-mono text-indigo-400">edge_weight × target_log₂FC</span>
          (fallback edge weight = 0.3), propagating the target's dysregulation signal one hop.
        </p>
      </div>
    ),
  },
];

const MethodCard: React.FC<{ method: Method }> = ({ method }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rounded-xl border transition-all duration-200 ${
        expanded
          ? 'border-indigo-700 bg-indigo-950/40 shadow-sm'
          : 'border-gray-700 bg-gray-800 hover:border-indigo-700 hover:shadow-sm'
      }`}
    >
      <button
        className="w-full flex gap-4 p-4 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white mt-0.5">
          {method.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-100 text-sm">{method.title}</p>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">{method.summary}</p>
        </div>
        <div className="flex-shrink-0 ml-2 mt-1 text-indigo-400">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 ml-13 border-t border-indigo-800 pt-3">
          <div className="ml-13 pl-[52px]">{method.detail}</div>
        </div>
      )}
    </div>
  );
};

const AboutModal: React.FC<AboutModalProps> = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl bg-gray-900">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6 rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/15 rounded-xl">
              <Dna className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">RNAverse</h2>
              <p className="text-blue-100 text-sm mt-0.5">
                AI-powered non-coding RNA therapeutic target discovery
              </p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* About section */}
          <section className="space-y-4">
            <p className="text-white font-semibold leading-relaxed text-[15px]">
              55M+ single cells. 120,000 gene regulatory networks.
              186 diseases. 165 datasets. One conversation.
            </p>
            <p className="text-gray-300 leading-relaxed">
              For decades, most of the human genome was considered noise. It isn't.
              Non-coding RNA drives disease, regulates genes, and determines therapeutic
              outcomes. It has never been searchable at scale. RNAverse changes that.
            </p>
            <p className="text-gray-300 leading-relaxed">
              RNAverse distills over 55 million human cells into insights.
              Each query draws on regulatory networks, disease-specific expression
              signatures, and cross-tissue comparisons, surfacing high-specificity therapeutic
              candidates in seconds.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Ask a question. Get ranked targets grounded in single-cell data, network
              topology, and in silico perturbation. No pipelines. No scripting. No waiting.
            </p>
          </section>

          {/* Authors */}
          <section>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Authors
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {authors.map((author) => (
                <div
                  key={author.name}
                  className="rounded-xl border border-gray-700 bg-gray-800 p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
                >
                  <div
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${author.gradient} flex items-center justify-center`}
                  >
                    <span className="text-white text-sm font-bold">{author.initials}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-100 text-sm">{author.name}</p>
                    <p className="text-xs text-indigo-400 font-medium mt-0.5">{author.role}</p>
                    <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                      {author.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Methods */}
          <section>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Methods
            </h3>
            <p className="text-xs text-gray-400 mb-4">Click any method to expand details.</p>
            <div className="space-y-3">
              {methods.map((m) => (
                <MethodCard key={m.title} method={m} />
              ))}
            </div>
          </section>

          {/* Data sources */}
          <section>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Data Sources
            </h3>
            <div className="flex flex-wrap gap-2">
              {[
                'CZ CELLxGENE Census',
                'GENCODE v44',
                'NCBI Gene',
                'PubMed / PMC',
                'Ensembl',
                'scGPT',
              ].map((src) => (
                <span
                  key={src}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-950/50 to-purple-950/50 text-indigo-300 border border-indigo-700"
                >
                  {src}
                </span>
              ))}
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-700 pt-4 space-y-1">
            <p className="text-xs text-gray-400">
              RNAverse — Genomic Intelligence
            </p>
            <p className="text-xs text-gray-500">
              Built by LincSwitch Therapeutics
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutModal;
